/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Docs for AI tools", description = "Search and read Hightouch documentation from AI tools via MCP, plain-text files, and agent skills.", lastUpdatedDate = new Date(1783468800000), keywords = "mcp, model context protocol, llms.txt, ai, agent, claude, cursor, chatgpt, copilot, llm, agent skills";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Docs for AI tools",
        description: "Search and read Hightouch documentation from AI tools via MCP, plain-text files, and agent skills.",
        lastUpdatedDate: "Wed Jul 08 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "MCP server",
          "depth": 0,
          "id": "mcp-server"
        }, {
          "value": "Endpoint",
          "depth": 1,
          "id": "endpoint"
        }, {
          "value": "Available tools",
          "depth": 1,
          "id": "available-tools"
        }, {
          "value": "Configuration",
          "depth": 1,
          "id": "configuration"
        }, {
          "value": "Example prompts",
          "depth": 1,
          "id": "example-prompts"
        }, {
          "value": "Plain-text files",
          "depth": 0,
          "id": "plain-text-files"
        }, {
          "value": "Skill file",
          "depth": 0,
          "id": "skill-file"
        }, {
          "value": "Additional resources",
          "depth": 0,
          "id": "additional-resources"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      h3: "h3",
      pre: "pre",
      code: "code",
      span: "span",
      h4: "h4",
      h5: "h5",
      ol: "ol",
      li: "li",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch publishes documentation in formats that AI tools can read directly, so they have accurate and current information when answering questions or helping you build integrations. Choose the format that fits how your tool works:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Format"
            }), _jsx(_components.th, {
              align: "left",
              children: "Best for"
            }), _jsx(_components.th, {
              align: "left",
              children: "How it works"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#mcp-server",
                children: "MCP server"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Targeted lookups from tools that support MCP"
            }), _jsx(_components.td, {
              align: "left",
              children: "The tool searches for relevant sections and reads only the pages it needs. Uses fewer tokens because it doesn't load everything upfront."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#plain-text-files",
                children: "Plain-text files"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Broad context loading or tools without MCP support"
            }), _jsx(_components.td, {
              align: "left",
              children: "Paste a URL to give your tool an index of all pages or the full content of every page in one file."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.a, {
                href: "#skills",
                children: "Skills"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Implementation tasks (API calls, SDK setup, sync configuration)"
            }), _jsx(_components.td, {
              align: "left",
              children: "A structured instruction file that helps tools work with the right endpoints, patterns, and parameters."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "mcp-server",
        children: "MCP server"
      }), "\n", _jsxs(_components.p, {
        children: ["For AI tools that support ", _jsx(_components.a, {
          href: "https://modelcontextprotocol.io",
          children: "Model Context Protocol"
        }), " (MCP), Hightouch publishes an MCP endpoint with two tools: one to search across all public docs by keyword, and one to read the full content of a specific page. The tool retrieves only the sections relevant to your question rather than loading all documentation at once, which means faster responses and lower token usage."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This is a ", _jsx(_components.strong, {
            children: "docs-only"
          }), " MCP server for searching and reading Hightouch\ndocumentation — it doesn't connect to your Hightouch workspace or take actions\nin your account. For an AI assistant that can build audiences, design\njourneys, and generate emails, ad creatives, and campaign concepts directly in\nyour workspace, see the ", _jsx(_components.a, {
            href: "/ai-integrations/mcp",
            children: "Hightouch MCP"
          }), " instead."]
        })
      }), "\n", _jsx(_components.p, {
        children: "The search index is rebuilt on every deployment, so results always reflect the latest content."
      }), "\n", _jsx(_components.h3, {
        id: "endpoint",
        children: "Endpoint"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ruby",
          children: [_jsx(_components.span, {
            className: "hljs-symbol",
            children: "https:"
          }), "/", _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/hightouch.com/docs"
          }), _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/api/mcp"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "available-tools",
        children: "Available tools"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Tool"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "search_docs"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Full-text search across all public docs with per-heading granularity. Returns matching sections with title, URL, content snippet, and section hierarchy. Supports filtering by product area."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "read_doc"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Returns the full markdown content of a documentation page by path. Use paths from ", _jsx(_components.code, {
                children: "search_docs"
              }), " results."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "configuration",
        children: "Configuration"
      }), "\n", _jsx(_components.p, {
        children: "Any MCP client that supports HTTP transport can connect using the endpoint URL. Below are setup instructions for common tools."
      }), "\n", _jsx(_components.h4, {
        id: "claude",
        children: "Claude"
      }), "\n", _jsx(_components.h5, {
        id: "custom-connectors-recommended",
        children: "Custom connectors (recommended)"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "If you're an Owner on a Team or Enterprise plan:"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://claude.ai/admin-settings/connectors",
            children: "Organization settings → Connectors"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Hover over ", _jsx(_components.strong, {
            children: "Custom"
          }), ", then select ", _jsx(_components.strong, {
            children: "Web"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste the URL: ", _jsx(_components.code, {
            children: "https://hightouch.com/docs/api/mcp"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add"
          }), " to finish — no OAuth client ID or secret is needed since the endpoint doesn't require authentication."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "If you're a member (not an Owner) on a Team or Enterprise plan:"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Ask an Owner to complete the steps above first — members can't add custom connectors directly."
        }), "\n", _jsxs(_components.li, {
          children: ["Once it's added, go to ", _jsx(_components.a, {
            href: "https://claude.ai/customize/connectors",
            children: "Customize → Connectors"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Find \"Hightouch Docs\" in the list (labeled ", _jsx(_components.strong, {
            children: "Custom"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Connect"
          }), " to authenticate and start using it."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "If you're on an individual Pro, Max, or Free plan:"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://claude.ai/customize/connectors",
            children: "Customize → Connectors"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "+"
          }), ", then ", _jsx(_components.strong, {
            children: "Add custom connector"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste the URL: ", _jsx(_components.code, {
            children: "https://hightouch.com/docs/api/mcp"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add"
          }), " to finish."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Free plan accounts are limited to one custom connector total. If you already\nhave one configured, remove it before adding this one."
        })
      }), "\n", _jsx(_components.h5, {
        id: "config-file-if-you-cant-add-custom-connectors",
        children: "Config file (if you can't add custom connectors)"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Quit Claude Desktop completely."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Open the config file:"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["macOS: ", _jsx(_components.code, {
                children: "~/Library/Application Support/Claude/claude_desktop_config.json"
              })]
            }), "\n", _jsxs(_components.li, {
              children: ["Windows: ", _jsx(_components.code, {
                children: "%APPDATA%\\Claude\\claude_desktop_config.json"
              })]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Add (or merge into) the ", _jsx(_components.code, {
              children: "mcpServers"
            }), " object:"]
          }), "\n", _jsx(_components.pre, {
            children: _jsxs(_components.code, {
              className: "hljs language-json",
              children: [_jsx(_components.span, {
                className: "hljs-punctuation",
                children: "{"
              }), "\n  ", _jsx(_components.span, {
                className: "hljs-attr",
                children: "\"mcpServers\""
              }), _jsx(_components.span, {
                className: "hljs-punctuation",
                children: ":"
              }), " ", _jsx(_components.span, {
                className: "hljs-punctuation",
                children: "{"
              }), "\n    ", _jsx(_components.span, {
                className: "hljs-attr",
                children: "\"hightouch-docs\""
              }), _jsx(_components.span, {
                className: "hljs-punctuation",
                children: ":"
              }), " ", _jsx(_components.span, {
                className: "hljs-punctuation",
                children: "{"
              }), "\n      ", _jsx(_components.span, {
                className: "hljs-attr",
                children: "\"type\""
              }), _jsx(_components.span, {
                className: "hljs-punctuation",
                children: ":"
              }), " ", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"url\""
              }), _jsx(_components.span, {
                className: "hljs-punctuation",
                children: ","
              }), "\n      ", _jsx(_components.span, {
                className: "hljs-attr",
                children: "\"url\""
              }), _jsx(_components.span, {
                className: "hljs-punctuation",
                children: ":"
              }), " ", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"https://hightouch.com/docs/api/mcp\""
              }), "\n    ", _jsx(_components.span, {
                className: "hljs-punctuation",
                children: "}"
              }), "\n  ", _jsx(_components.span, {
                className: "hljs-punctuation",
                children: "}"
              }), "\n", _jsx(_components.span, {
                className: "hljs-punctuation",
                children: "}"
              }), "\n"]
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Save the file and relaunch Claude Desktop."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Confirm the connector is live: open a chat, click the tools/connector icon, and check that \"hightouch-docs\" appears with ", _jsx(_components.code, {
              children: "search_docs"
            }), " and ", _jsx(_components.code, {
              children: "read_doc"
            }), " listed."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Claude Code"
        }), " — Run the following from your terminal:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "claude mcp add --transport http hightouch-docs https://hightouch.com/docs/api/mcp\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Verify with ", _jsx(_components.code, {
          children: "claude mcp list"
        }), " — you should see ", _jsx(_components.code, {
          children: "hightouch-docs"
        }), " with a connected status."]
      }), "\n", _jsx(_components.h4, {
        id: "cursor",
        children: "Cursor"
      }), "\n", _jsxs(_components.p, {
        children: ["Add the following to ", _jsx(_components.code, {
          children: ".cursor/mcp.json"
        }), " in your project:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"mcpServers\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"hightouch-docs\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"url\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"url\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://hightouch.com/docs/api/mcp\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "other-clients",
        children: "Other clients"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the endpoint URL ", _jsx(_components.code, {
          children: "https://hightouch.com/docs/api/mcp"
        }), " with Streamable HTTP transport. No authentication is required. Refer to your client's documentation for MCP configuration instructions."]
      }), "\n", _jsx(_components.h3, {
        id: "example-prompts",
        children: "Example prompts"
      }), "\n", _jsx(_components.p, {
        children: "Once connected, your AI tool queries the docs automatically when you ask questions about Hightouch. Here are some examples to try:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Sync configuration"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["How ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "do"
          }), " I ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "set"
          }), " up a ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "sync"
          }), " to Salesforce? What ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "sync"
          }), " modes does it support?\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["What", _jsx(_components.span, {
            className: "hljs-comment",
            children: "'s the difference between upsert and mirror sync modes?"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Destinations and sources"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["What permissions do ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "I"
          }), " need ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " connect ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " Snowflake source?\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-rust",
          children: ["Show me the field mapping options ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "the"
          }), " Braze destination.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Troubleshooting"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: [_jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "I"
          }), "'m getting ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " \"rate limit exceeded\" error syncing ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " HubSpot. What should ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "I"
          }), " check?\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["How do ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "I"
          }), " use the Live Debugger ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " inspect API requests for ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " sync?\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Customer Studio"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["How do ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "I"
          }), " create ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " computed trait that counts purchases in the last ", _jsx(_components.span, {
            className: "hljs-number",
            children: "30"
          }), " days?\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["What ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "filter"
          }), " types ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "are"
          }), " available ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " the audience builder?\n"]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The docs MCP server gives your AI tool access to Hightouch documentation. To\nalso connect your AI assistant to your Hightouch workspace — to build\naudiences, create journeys, or generate content — add the ", _jsx(_components.a, {
            href: "/ai-integrations/mcp",
            children: "Hightouch\nMCP"
          }), ". You can use both together for documentation\nreference and platform actions in the same conversation."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "plain-text-files",
        children: "Plain-text files"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch publishes plain-text versions of the docs that any AI tool can read without parsing web page markup. Both files are regenerated on every deployment, so they always reflect the latest content. Use these when your tool doesn't support MCP or when you want to load broad context in a single request."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://hightouch.com/docs/llms.txt",
          children: _jsx(_components.strong, {
            children: "llms.txt"
          })
        }), " is an index of every public page, organized by product area. Use this when you want an AI tool to know what documentation is available."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://hightouch.com/docs/llms-full.txt",
          children: _jsx(_components.strong, {
            children: "llms-full.txt"
          })
        }), " contains the full text of every public page in a single file. Use this when you want an AI tool to have complete context about Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: "To use either file, paste the URL into your AI tool:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ruby",
          children: [_jsx(_components.span, {
            className: "hljs-symbol",
            children: "https:"
          }), "/", _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/hightouch.com/docs"
          }), _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/llms.txt\nhttps:/"
          }), _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/hightouch.com/docs"
          }), _jsx(_components.span, {
            className: "hljs-regexp",
            children: "/llms-full.txt\n"
          })]
        })
      }), "\n", _jsx(_components.h2, {
        id: "skill-file",
        children: "Skill file"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch publishes a ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/skill.md",
          children: "skill file"
        }), " that gives AI tools a structured reference for writing code against the Hightouch platform. It covers REST API endpoints and authentication, CLI commands, Events SDK methods and setup snippets, sync modes and schedule types, and core data model concepts (sources, models, destinations, syncs)."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use this when you want an AI tool to generate correct API calls, CLI commands, or SDK integrations without searching the full docs. The file follows the ", _jsx(_components.a, {
          href: "https://agentskills.io/",
          children: "Agent Skills"
        }), " spec."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The skill file is a static reference for developer tooling. To connect an AI\nassistant to your Hightouch workspace for building audiences, creating\njourneys, or generating content, see ", _jsx(_components.a, {
            href: "/ai-integrations/mcp",
            children: "Hightouch MCP"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "additional-resources",
        children: "Additional resources"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/developer-tools/api-guide",
            children: "API overview"
          }), " — REST API authentication and usage"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/api-reference",
            children: "API reference"
          }), " — Full API endpoint documentation"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/developer-tools/cli-guide",
            children: "CLI overview"
          }), " — Command line interface for Hightouch"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/events/overview",
            children: "Events SDK"
          }), " — Track user events for real-time activation"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
